import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

/**
 * The coaching line shown over the camera during performance — the agent's
 * transcript when realtime is running, the legacy socket transcript otherwise.
 *
 * Positioning is the caller's: the line sits at the bottom edge of the *camera*
 * pane, which is the page bottom on desktop, the card bottom in landscape and the
 * half-height mark in the portrait stack. Each layout branch in the exercise page
 * passes the anchor as `containerStyle`; only the pill itself lives here.
 *
 * `latency` is accepted in two shapes on purpose, because both systems can be
 * live at once during the migration:
 *   - `true`  → the old boolean "audio is lagging" warning
 *   - object  → a realtime measurement, rendered as a millisecond figure
 */
const InPerformanceText = ({ text, hideText, latency, containerStyle, isVisible = true }) => {
  const { i18n } = useTranslation();
  // Arabic-only: the default sans stack renders thin/small in Arabic here,
  // so the subtitle gets a clearer system font and a larger size. Other
  // languages and every other piece of text on the page are unaffected.
  const isArabic = i18n.language?.startsWith('ar');
  const showLegacyLatencyWarning = latency === true;
  const latencyLabel =
    typeof latency?.latencyMs === 'number' ? `${Math.round(latency.latencyMs)} ms` : null;

  if (hideText || !text) {
    return null;
  }

  return (
    <div
      css={[
        tw`pointer-events-none z-[300] w-fit max-w-[calc(100vw-24px)] text-center transition-opacity duration-700 ease-out`,
        // Faded rather than unmounted the instant the agent stops talking — an
        // abrupt cut read as the line getting stuck, not as it finishing.
        isVisible ? tw`opacity-100` : tw`opacity-0`,
        containerStyle,
      ]}
    >
      <p
        css={[
          tw`rounded-[14px] bg-black/70 px-5 py-3 font-[600] leading-snug text-white shadow-[0_10px_30px_rgba(0,0,0,0.35)]`,
          isArabic ? tw`text-[26px] max-lg:text-[20px]` : tw`text-[18px] max-lg:text-[15px]`,
        ]}
        style={{
          textShadow: '0 1px 3px rgba(0,0,0,0.6)',
          ...(isArabic && { fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif' }),
        }}
      >
        {text}
      </p>

      {showLegacyLatencyWarning && (
        <p tw="mt-1 text-[12px] font-[600] text-[#FCA5A5]">Audio is running behind</p>
      )}
      {latencyLabel && (
        <p tw="mt-1 text-[12px] font-[600] text-white/60">Response latency: {latencyLabel}</p>
      )}
    </div>
  );
};

export default memo(InPerformanceText);
